
// Store
import { createVuexHelpers } from 'vue2-helpers'
// Components
import Badge from '@/components/Badge.vue'
import IconSvg from '@/components/IconSvg.vue'
import EventPeriod from '@/components/Ticketing/EventPeriod.vue'
import EventDateIcon from '@/components/Ticketing/EventDateIcon.vue'
import FormattedPrice from '@/components/FormattedPrice.vue'
// Vue
import { computed, defineComponent, PropType } from 'vue'
// Router
import { useRouter, type Router, useRoute } from 'vue2-helpers/vue-router'
import { Route } from "vue-router";
// Types
import { FormattedEventEntity } from '@/types/entitiesTypes'
import { IsMobileScreenState } from '@/types/storeTypes'
// Constants
import { EventStatus, EventCategory } from '@/types/constantsTypes'

// Icons
import CanceledIcon from '@/assets/images/canceled_ribbon.svg'

export default defineComponent({
	name: 'EventCard',

	components: {
		FormattedPrice,
		EventDateIcon,
		EventPeriod,
		Badge,
		IconSvg
	},

	props: {
		event: {
			type: Object as PropType<FormattedEventEntity>,
			default: () => ({})
		}
	},

	setup({ event }) {
		// Router
		const router: Router = useRouter()
    const route: Route = useRoute()

		// Store
		const { useState } = createVuexHelpers()
		const { isMobileScreen }: IsMobileScreenState = useState('ui', [
			'isMobileScreen'
		])

		// Computed
    const canceledIcon = computed(() => CanceledIcon)
		const category = computed(() => EventCategory[event.categoryId])
		const isSingleTier = computed(() => event.eventTicketTypes.length === 1)
		const includesUnlimitedTier = computed(
			() =>
				Boolean(event.eventTicketTypes.find(type => type.ticketCount === null))
		)

		const soldOut = computed(() => {
			if (includesUnlimitedTier.value) return false

			const ticketsLeft = event.eventTicketTypes.reduce((acc, type) => {
				return acc + Number(type.ticketLeft)
			}, 0)

			return !ticketsLeft
		})
		const ticketPrice = computed(() => {
			if (isSingleTier.value) {
				return {
					min: event.eventTicketTypes[0].ticketPrice
				}
			}

			const pricesArray = Array.from(new Set(event.eventTicketTypes.map(type => Number(type.ticketPrice))))

			return {
				min: Math.min(...pricesArray),
				max: Math.max(...pricesArray)
			}
		})

    const isEventPastOrCanceled = computed(() => event.status !== EventStatus.ACTIVE)
    const isEventCanceled = computed(() => event.status === EventStatus.CANCELED)
		const photo = computed(() => event.eventImages[0]?.previewUrl)

		const isOpenAdmissionEvent = computed(() => !event.eventTicketTypes.length)

		// Methods
		const goToEventDetails = (): void => {
      if (isEventPastOrCanceled.value) return

      const query = route.query

			router.push({
				name: 'event-details',
				params: { eventId: event.id },
        query
			})
		}

		return {
			isMobileScreen,
			category,
			soldOut,
			photo,
      isEventPastOrCanceled,
      isEventCanceled,
      canceledIcon,
			ticketPrice,
			goToEventDetails,
			isOpenAdmissionEvent
		}
	}
})
